import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadIcon from '../../../img/upload-icon.svg';
import TrashIcon from '../../../img/delete-btn-icon.svg';

export interface AttachPdfPopupProps {
    open: boolean;
    savedFiles: File[];
    onClose: () => void;
    onUpload?: (files: File[]) => void;
    maxFiles?: number;
}

export const AttachPdfPopup: React.FC<AttachPdfPopupProps> = ({
    open,
    savedFiles,
    onClose,
    onUpload,
    maxFiles = 6,
}) => {
    const [files, setFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [fileError, setFileError] = useState<string>("");

    const inputRef = useRef<HTMLInputElement | null>(null);

    const filesCount = files.length;
    const limitReached = filesCount >= maxFiles;

    const MAX_TOTAL_BYTES = 4 * 1024 * 1024;

    useEffect(() => {
        if (open) setFiles(savedFiles ?? []);
    }, [open, savedFiles]);

    const addFiles = useCallback(
        (incoming: FileList | File[]) => {
            setFileError("");
            const arr = Array.from(incoming);

            const pdfs = arr.filter(
                (f) =>
                    f.type === "application/pdf" ||
                    f.name.toLowerCase().endsWith(".pdf")
            );

            const remaining = Math.max(0, maxFiles - files.length);
            if (remaining <= 0) return;

            const existingSize = files.reduce((sum, f) => sum + f.size, 0);
            const newSize = pdfs.reduce((sum, f) => sum + f.size, 0);
            const totalSize = existingSize + newSize;

            if (totalSize >= MAX_TOTAL_BYTES) {
                setFileError("Total size of all uploaded files cannot exceed 4 MB.");
                return;
            }

            const next = [...files, ...pdfs.slice(0, remaining)];
            setFiles(next);
        },
        [files, maxFiles]
    );

    const handleBrowse = () => inputRef.current?.click();

    const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        if (e.target.files) addFiles(e.target.files);
        e.target.value = "";
    };

    const handleDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
    };

    const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragging) setIsDragging(true);
    };

    const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const removeAt = (idx: number) => {
        const next = files.slice();
        next.splice(idx, 1);
        setFiles(next);
    };

    const handleUpload = () => {
        onUpload?.(files);
        setFiles([]);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            aria-labelledby="attach-title"
            PaperProps={{
                sx: {
                    width: 540,
                    maxWidth: "100%",
                },
            }}
        >
            <DialogTitle id="attach-title">
                <IconButton
                    onClick={onClose}
                    aria-label="Close"
                    size="small"
                    sx={{ position: "absolute", right: 12, top: 12 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ padding: "40px" }}>
                <Typography
                    sx={{
                        mt: 2,
                        p: 2,
                        fontWeight: "600",
                        fontSize: "32px",
                        textAlign: "center",
                    }}
                >
                    Upload
                </Typography>
                <Paper
                    elevation={0}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    sx={{
                        padding: "30px",
                        background: isDragging ? "#f1f1f1" : "#F7FCFF",
                        border: "1px dashed",
                        borderColor: "primary.light",
                        borderRadius: 1,
                        textAlign: "center",
                    }}
                >
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <img src={UploadIcon}  alt=""/>
                        <Typography
                            fontWeight={700}
                            fontSize={16}
                            lineHeight="24px"
                            mt={3}
                            className="uploading-files-title"
                        >
                            Drag &amp; drop files or{" "}
                            <Typography
                                component="span"
                                onClick={handleBrowse}
                                sx={{
                                    color: "#62B5E5",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                }}
                            >
                                Browse
                            </Typography>
                        </Typography>

                        <input
                            ref={inputRef}
                            type="file"
                            accept=".pdf,application/pdf"
                            multiple
                            hidden
                            onChange={handleInputChange}
                        />

                        {limitReached && (
                            <Typography
                                fontSize={10}
                                fontStyle={"italic"}
                                color={"red !important"}
                                fontFamily={"Montserrat"}
                            >
                                {`You have reached your file limit of ${maxFiles} files.`}
                            </Typography>
                        )}

                        {fileError && (
                            <Typography
                                fontSize={10}
                                fontStyle={"italic"}
                                color={"red !important"}
                                fontFamily={"Montserrat"}
                                mt={1}
                            >
                                {fileError}
                            </Typography>
                        )}

                        <Typography fontSize={12} className="supported-formates">
                            Supported formats: PDF (max 4 MB total)
                        </Typography>
                    </Box>
                </Paper>

                <Box mt={2} textAlign="left">
                    {files.length > 0 && (
                        <>
                            <Typography
                                variant="subtitle2"
                                fontSize={"14px !important"}
                                color="#676767"
                                fontWeight={700}
                                className="uploading-files-title"
                            >
                                Uploading – {files.length}/{files.length} Uploaded
                            </Typography>
                            <List sx={{ maxHeight: 200, overflow: "auto" }}>
                                {files.map((f, idx) => (
                                    <ListItem
                                        key={`${f.name}-${idx}`}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "success.light",
                                            borderRadius: 1,
                                            mb: 1,
                                            py: 1,
                                        }}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                aria-label="remove"
                                                onClick={() => removeAt(idx)}
                                            >
                                                <img src={TrashIcon} width={24} height={24} alt=""/>
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText className="file-name" primary={f.name} />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}

                    <Button
                        fullWidth
                        className="blueButtonOutline filledBlueButton"
                        sx={{ mt: 2 }}
                        disabled={files.length === 0}
                        onClick={handleUpload}
                    >
                        Upload Files
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
